import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import './ListaAnimales.css';
import API_CONFIG from '../config';
import { apiFetch } from '../auth';
import { colorBg } from '../colorCaravana';
import FichaAnimal from './FichaAnimal';
import AyudaModal from './AyudaModal';
import { AYUDA_RODEO } from '../ayudaContenido';

const BASE = API_CONFIG.BASE_URL + API_CONFIG.ENDPOINTS.ANIMALES;

const CLASIFICACION_LABEL = {
  VACA: 'Vaca', VAQUILLA: 'Vaquilla', NOVILLO: 'Novillo', TORO: 'Toro',
  DESM_MACHO: 'Desm. Macho', DESM_HEMBRA: 'Desm. Hembra',
  TERNERO_MACHO: 'Ternero Macho', TERNERO_HEMBRA: 'Ternero Hembra',
};

export default function ListaAnimales() {
  const [animales, setAnimales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});

  // Filtros
  const [filtros, setFiltros] = useState({ caravana: '', nombre: '', raza: '', sexo: '', estado: 'ACTIVO', colorCaravana: '', clasificacion: '' });
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Modal ficha
  const [animalId, setAnimalId] = useState(null);
  const [mostrarFicha, setMostrarFicha] = useState(false);
  const [mostrarAyuda, setMostrarAyuda] = useState(false);


  const cargarAnimales = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, size: 20 });
      if (filtros.caravana)       params.append('caravana',       filtros.caravana);
      if (filtros.nombre)         params.append('nombre',         filtros.nombre);
      if (filtros.raza)           params.append('raza',           filtros.raza);
      if (filtros.sexo)           params.append('sexo',           filtros.sexo);
      if (filtros.estado)         params.append('estado',         filtros.estado);
      if (filtros.colorCaravana)  params.append('colorCaravana',  filtros.colorCaravana);
      if (filtros.clasificacion)  params.append('clasificacion',  filtros.clasificacion);

      const res = await apiFetch(`${BASE}?${params}`);
      const data = await res.json();
      setAnimales(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
    } catch (e) {
      console.error('Error cargando animales:', e);
    } finally {
      setLoading(false);
    }
  }, [filtros, page]);

  const cargarStats = useCallback(async () => {
    try {
      const res = await apiFetch(`${BASE}/estadisticas`);
      setStats(await res.json());
    } catch (e) {}
  }, []);

  useEffect(() => { cargarAnimales(); }, [cargarAnimales]);
  useEffect(() => { cargarStats(); }, [cargarStats]);

  const handleFiltro = (e) => {
    setFiltros(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setPage(0);
  };

  const verFicha = (id) => {
    setAnimalId(id);
    setMostrarFicha(true);
  };

  const cerrarFicha = () => {
    setMostrarFicha(false);
    setAnimalId(null);
    cargarAnimales();
    cargarStats();
  };

  const formatFecha = (f) => f ? new Date(f).toLocaleDateString('es-PY') : '-';

  const exportarExcel = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: 0, size: 9999 });
      if (filtros.caravana)      params.append('caravana',      filtros.caravana);
      if (filtros.nombre)        params.append('nombre',        filtros.nombre);
      if (filtros.raza)          params.append('raza',          filtros.raza);
      if (filtros.sexo)          params.append('sexo',          filtros.sexo);
      if (filtros.estado)        params.append('estado',        filtros.estado);
      if (filtros.colorCaravana) params.append('colorCaravana', filtros.colorCaravana);
      if (filtros.clasificacion) params.append('clasificacion', filtros.clasificacion);

      const res = await apiFetch(`${BASE}?${params}`);
      const data = await res.json();
      const todos = data.content || [];

      const filas = todos.map(a => ({
        'ID':             a.id,
        'Caravana':       a.caravana || '',
        'Color Caravana': a.colorCaravana || '',
        'Clasificación':  CLASIFICACION_LABEL[a.clasificacion] || a.clasificacion || '',
        'Nombre':         a.nombre || '',
        'Sexo':           a.sexo || '',
        'Raza':           a.raza || '',
        'Color':          a.color || '',
        'Fecha Nac.':     a.fechaNacimiento ? new Date(a.fechaNacimiento).toLocaleDateString('es-PY') : '',
        'Peso (kg)':      a.peso || '',
        'Estado':         a.estado || '',
        'Madre Caravana': a.madreCaravana || '',
        'Madre Nombre':   a.madreNombre || '',
        'Padre Caravana': a.padreCaravana || '',
        'Padre Nombre':   a.padreNombre || '',
        'Cant. Crías':    a.cantidadCrias || 0,
        'Cant. Vacunas':  a.cantidadVacunas || 0,
        'Cant. Pesos':    a.cantidadPesos || 0,
        'Observaciones':  a.observaciones || '',
        'Fecha Registro': a.fechaRegistro ? new Date(a.fechaRegistro).toLocaleDateString('es-PY') : '',
      }));

      const ws = XLSX.utils.json_to_sheet(filas);
      ws['!cols'] = [
        {wch:6},{wch:12},{wch:14},{wch:16},{wch:16},{wch:8},{wch:12},{wch:10},
        {wch:12},{wch:10},{wch:10},{wch:14},{wch:16},{wch:14},{wch:16},
        {wch:10},{wch:12},{wch:12},{wch:24},{wch:14},
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Rodeo');
      const fecha = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `rodeo_${fecha}.xlsx`);
    } catch (e) {
      console.error('Error exportando:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="lista-animales">
      {/* Stats */}
      <div className="stats-row">
        <div className="stat-card">
          <span className="stat-num">{stats.totalActivos || 0}</span>
          <span className="stat-label">🐄 Activos</span>
        </div>
        <div className="stat-card">
          <span className="stat-num">{stats.totalVendidos || 0}</span>
          <span className="stat-label">💰 Vendidos</span>
        </div>
        <div className="stat-card">
          <span className="stat-num">{stats.totalMuertos || 0}</span>
          <span className="stat-label">💀 Muertos</span>
        </div>
        <div className="stat-card">
          <span className="stat-num">{stats.total || 0}</span>
          <span className="stat-label">📊 Total</span>
        </div>
      </div>

      {/* Toolbar */}
      <div className="card">
        <div className="toolbar">
          <h2>Rodeo</h2>
          <div style={{display:'flex', gap:'8px'}}>
            <button className="btn-ayuda" onClick={() => setMostrarAyuda(true)}>
              ❓ Ayuda
            </button>
            <button className="btn-success" onClick={exportarExcel} disabled={loading}>
              📥 Exportar Excel
            </button>
            <button className="btn-primary" onClick={() => { setAnimalId(null); setMostrarFicha(true); }}>
              + Nuevo Animal
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="filtros">
          <input name="caravana" placeholder="🏷 Caravana" value={filtros.caravana} onChange={handleFiltro} />
          <input name="nombre"   placeholder="🐄 Nombre"   value={filtros.nombre}   onChange={handleFiltro} />
          <input name="raza"     placeholder="🧬 Raza"     value={filtros.raza}     onChange={handleFiltro} />
          <select name="sexo" value={filtros.sexo} onChange={handleFiltro}>
            <option value="">Todos los sexos</option>
            <option value="MACHO">Macho</option>
            <option value="HEMBRA">Hembra</option>
          </select>
          <select name="estado" value={filtros.estado} onChange={handleFiltro}>
            <option value="">Todos los estados</option>
            <option value="ACTIVO">Activo</option>
            <option value="VENDIDO">Vendido</option>
            <option value="MUERTO">Muerto</option>
            <option value="TRANSFERIDO">Transferido</option>
          </select>
          <input name="colorCaravana" placeholder="🏷 Color caravana" value={filtros.colorCaravana} onChange={handleFiltro} />
          <select name="clasificacion" value={filtros.clasificacion} onChange={handleFiltro}>
            <option value="">Todas las clasificaciones</option>
            <option value="VACA">Vaca</option>
            <option value="VAQUILLA">Vaquilla</option>
            <option value="NOVILLO">Novillo</option>
            <option value="TORO">Toro</option>
            <option value="DESM_MACHO">Desm. Macho</option>
            <option value="DESM_HEMBRA">Desm. Hembra</option>
            <option value="TERNERO_MACHO">Ternero Macho</option>
            <option value="TERNERO_HEMBRA">Ternero Hembra</option>
          </select>
          <button className="btn-secondary btn-sm" onClick={() => {
            setFiltros({ caravana: '', nombre: '', raza: '', sexo: '', estado: '', colorCaravana: '', clasificacion: '' });
            setPage(0);
          }}>Limpiar</button>
        </div>
      </div>

      {/* Tabla */}
      <div className="card">
        {loading ? (
          <div className="loading">Cargando...</div>
        ) : (
          <>
            <div className="table-info">{totalElements} animales encontrados</div>
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Caravana</th>
                  <th>Color Car.</th>
                  <th>Clasificación</th>
                  <th>Nombre</th>
                  <th>Sexo</th>
                  <th>Raza</th>
                  <th>Fecha Nac.</th>
                  <th>Peso (kg)</th>
                  <th>Madre</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {animales.length === 0 ? (
                  <tr><td colSpan="12" style={{textAlign:'center', color:'#999', padding:'30px'}}>
                    No hay animales registrados
                  </td></tr>
                ) : animales.map(a => (
                  <tr key={a.id}>
                    <td style={{color:'#999', fontSize:'0.85rem'}}>{a.id}</td>
                    <td><strong>{a.caravana}</strong></td>
                    <td>
                      {a.colorCaravana
                        ? <span className="color-tag" style={{background: colorBg(a.colorCaravana)}}>{a.colorCaravana}</span>
                        : '-'}
                    </td>
                    <td>{a.clasificacion ? CLASIFICACION_LABEL[a.clasificacion] || a.clasificacion : '-'}</td>
                    <td>{a.nombre || '-'}</td>
                    <td>
                      <span className={`badge badge-${a.sexo?.toLowerCase()}`}>
                        {a.sexo === 'MACHO' ? '♂' : '♀'} {a.sexo}
                      </span>
                    </td>
                    <td>{a.raza || '-'}</td>
                    <td>{formatFecha(a.fechaNacimiento)}</td>
                    <td>{a.peso ? `${a.peso} kg` : '-'}</td>
                    <td>{a.madreCaravana ? `${a.madreCaravana}` : '-'}</td>
                    <td>
                      <span className={`badge badge-${a.estado?.toLowerCase()}`}>
                        {a.estado}
                      </span>
                    </td>
                    <td>
                      <button className="btn-primary btn-sm" onClick={() => verFicha(a.id)}>
                        Ver Ficha
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Paginación */}
            {totalPages > 1 && (
              <div className="paginacion">
                <button disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Anterior</button>
                <span>Página {page + 1} de {totalPages}</span>
                <button disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Siguiente →</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Ayuda */}
      {mostrarAyuda && (
        <AyudaModal titulo="Ayuda — Rodeo" pasos={AYUDA_RODEO} onClose={() => setMostrarAyuda(false)} />
      )}

      {/* Modal Ficha */}
      {mostrarFicha && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && cerrarFicha()}>
          <div className="modal modal-xl">
            <FichaAnimal
              animalId={animalId}
              onVolver={cerrarFicha}
              onVerCria={(id) => { setAnimalId(id); }}
              modal={true}
            />
          </div>
        </div>
      )}
    </div>
  );
}
